import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'redux';
import AssetTable from '../../components/Asset/AssetTable';
import AssetGrid from '../../components/Asset/AssetGrid';
import AssetMap from '../../components/AssetMap';
import AssetOverlay from '../../components/Asset/AssetOverlay';
import DependencyMap, {SpofPanel} from '../../components/Asset/DependencyMap';
import { getInventory, deleteAsset, getAssetStats, duplicateAsset } from '../../store/actions/assetActions';
import Popup from 'reactjs-popup';
import 'reactjs-popup/dist/index.css';
import { CheckCircle, AlertCircle, Box, DollarSign, Plus, Link2, TrendingUp } from "lucide-react";
import AssetMetric from "../../components/Charts/General/AssetMetric"
import AssetStatusMetric from "../../components/Charts/General/AssetStatusMetric"
import AssetQuarterMetric from "../../components/Charts/General/AssetQuarterMetric"
import TopAssets from "../../components/Charts/General/TopAssets"

const ITInventory = ({ department_id, assets, loading, error, getInventoryFN, deleteAssetFN, duplicateAssetFN, sidebar_collapsed, activeassetforworkflow, setActiveAssetForWorkflow, getAssetStatsFN, assets_data }) => {
    const { t } = useTranslation();
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [viewOption, setviewOption] = useState("Assets");

    const [assetViewMode, setAssetViewMode] = useState("table");
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const categoryOptions = [
        'Hardware',
        'Software',
        'Services',
        'Virtual Machine'
    ];

    

    // assets -> filteredAssets for table
    const filteredAssets = useMemo(() => {
        const list = Array.isArray(assets) ? assets : [];
        const q = searchQuery.trim().toLowerCase();

        return list.filter((asset) => {
            if (statusFilter && asset.status !== statusFilter) return false;

            if (!q) return true;

            const haystack = [
                asset.id && String(asset.id),
                asset.name,
                asset.category,
                asset.location,
                asset.department,
                asset.owner_name,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return haystack.includes(q);
        });
    }, [assets, searchQuery, statusFilter]);


    useEffect(() => {
        getInventoryFN(department_id).finally(() => setIsInitialLoad(false));
    }, [getInventoryFN, department_id]);

    useEffect(() => {
        getAssetStatsFN(department_id).finally(() => setIsInitialLoad(false));
    }, [getAssetStatsFN, department_id]);

    useEffect(() => {
        if (activeassetforworkflow) {
            setSelectedAsset(activeassetforworkflow);
            setIsOverlayOpen(true);
            setActiveAssetForWorkflow(null);
        }
    }, [activeassetforworkflow, setActiveAssetForWorkflow]);

    const handleAddNew = () => {
        setSelectedAsset(null);
        setIsOverlayOpen(true);
        getAssetStatsFN(department_id);
    };

     const handleClose = () => {
        setIsOverlayOpen(false)
        getInventoryFN(department_id);
    };

    const handleEdit = (asset) => {
        setSelectedAsset(asset);
        setIsOverlayOpen(true);
        getAssetStatsFN(department_id);
    };

    const handleDelete = async (assetId) => {
        await deleteAssetFN(assetId);
        getInventoryFN(department_id);
    };

    const handleDuplicate = async (assetId) => {
        await duplicateAssetFN(assetId);
        getInventoryFN(department_id);
    };


    const handleViewStats = () => {
        setviewOption("Stats");
        getAssetStatsFN(department_id);
    };

    const handleViewMap = () => {
        setviewOption("Map");
        getAssetStatsFN(department_id);
    };

    const handleViewAssets = () => {
        setviewOption("Assets")
    }




    if (isInitialLoad && loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <p>Loading assets...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center h-screen text-red-600">
                <p>Error fetching assets: {error}</p>
            </div>
        );
    }


    return (
        <div className="w-full min-h-screen bg-[#F8F8F8] flex flex-col">
            {/* inner scroll area */}
            <div className="flex-1 overflow-y-auto">
                <div className="relative px-6 md:px-10 xl:px-16 2xl:px-24 pt-6 pb-40">

                    {/* ASSETS VIEW */}
                    {viewOption === "Assets" && (
                        <>
                           

                                {/* Header row: title + pill toggle + Add button */}
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h1 className="text-xl md:text-2xl font-medium tracking-wide text-[#0E0E0E]">
                                            {t("assetManagement.title")}
                                        </h1>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {t("assetManagement.subtitle")}
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-2">

                                            <button
                                                onClick={handleViewAssets}
                                                className={`
      flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition
      ${viewOption === "Assets"
                                                        ? "bg-[#0F0F19] text-white"
                                                        : "bg-white text-gray-700 hover:bg-gray-100"
                                                    }
    `}
                                            >
                                                {/* Icon */}
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                                                </svg>
                                                <span>{t("assetManagement.tabs.assets")}</span>
                                            </button>


                                            <button
                                                onClick={handleViewMap}
                                                className={`
      flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition
      ${viewOption === "Stats"
                                                        ? "bg-[#0F0F19] text-white"
                                                        : "bg-white text-gray-700 hover:bg-gray-100"
                                                    }
    `}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M5 13l4 4L19 7"
                                                    />
                                                </svg>
                                                <span>{t("assetManagement.tabs.dependencyMap")}</span>
                                            </button>




                                            <button
                                                onClick={handleViewStats}
                                                className={`
      flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition
      ${viewOption === "Stats"
                                                        ? "bg-[#0F0F19] text-white"
                                                        : "bg-white text-gray-700 hover:bg-gray-100"
                                                    }
    `}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M5 13l4 4L19 7"
                                                    />
                                                </svg>
                                                <span>{t("assetManagement.tabs.stats")}</span>
                                            </button>
                                        </div>




                                    </div>
                                </div>

                                {/* Stat cards row */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">

                                    {/* Total Assets */}
                                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4 flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-full bg-[#EFF6FF] flex items-center justify-center">
                                            <Box className="w-5 h-5 text-[#3B82F6]" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">{t("assetManagement.summary.totalAssets")}</p>
                                            <p className="text-2xl text-[#111827] mt-1">{assets?.length ?? 0}</p>
                                        </div>
                                    </div>

                                    {/* Total Value */}
                                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4 flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-full bg-[#ECFDF5] flex items-center justify-center">
                                            <DollarSign className="w-5 h-5 text-[#10B981]" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">{t("assetManagement.summary.totalValue")}</p>
                                            <p className="text-2xl text-[#111827] mt-1">
                                                $
                                                {(assets || [])
                                                    .reduce((sum, a) => sum + (Number(a.value) || 0), 0)
                                                    .toLocaleString()}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Active */}
                                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4 flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-full bg-[#ECFDF5] flex items-center justify-center">
                                            <CheckCircle className="w-5 h-5 text-[#10B981]" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">{t("assetManagement.summary.active")}</p>
                                            <p className="text-2xl text-[#111827] mt-1">
                                                {(assets || []).filter((a) => a.status === "active")?.length}
                                            </p>
                                        </div>
                                    </div>

                                    {/* In Maintenance */}
                                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4 flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-full bg-[#FFF7ED] flex items-center justify-center">
                                            <AlertCircle className="w-5 h-5 text-[#F59E0B]" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">{t("assetManagement.summary.inMaintenance")}</p>
                                            <p className="text-2xl text-[#111827] mt-1">
                                                {(assets || []).filter((a) => a.status === "pending")?.length}
                                            </p>
                                        </div>
                                    </div>

                                </div>


                                {/* Search + filter wrapper */}
                                <div className="mb-5">
                                    <div className="bg-white rounded-xl border border-gray-200 px-5 py-3 flex flex-col md:flex-row items-center gap-4">
                                        {/* Search */}
                                        <div className="flex-1 max-w-[70%]">
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder={t("assetManagement.searchPlaceholder")}
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    className="
                                                w-full
                                                h-9
                                                pl-9 pr-3
                                                rounded-md
                                                bg-[#F3F4F6]
                                                text-sm text-gray-800
                                                focus:outline-none
                                                focus:ring-0
                                                border-none
                                            "
                                                    style={{
                                                        boxShadow: "none",
                                                        WebkitAppearance: "none",
                                                    }}
                                                />

                                                {/* Search icon */}
                                                <svg
                                                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M21 21l-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z"
                                                    />
                                                </svg>
                                            </div>
                                        </div>

                                        {/* Status filter */}
                                        <div className="relative w-full md:w-48">
                                            {/* Left icon */}
                                            <svg
                                                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                            >
                                                <path
                                                    d="M5 7h14M5 12h14M5 17h14"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                />
                                            </svg>

                                            <select
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                                className="
                                            appearance-none
                                            w-full
                                            bg-[#F3F4F6]
                                            rounded-md
                                            pl-8 pr-6 py-2
                                            text-xs md:text-sm
                                            border-none
                                            focus:outline-none
                                            focus:ring-0
                                            text-gray-800
                                        "
                                            >
                                                <option value="">{t("assetManagement.filters.allStatus")}</option>
                                                <option value="active">{t("assetManagement.filters.active")}</option>
                                                <option value="inactive">{t("assetManagement.filters.inactive")}</option>
                                                <option value="retired">{t("assetManagement.filters.retired")}</option>
                                                <option value="pending">{t("assetManagement.filters.inMaintenance")}</option>
                                            </select>

                                            {/* Right arrow */}
                                            <svg
                                                className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                            >
                                                <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
                                            </svg>
                                        </div>

                                        {/* View toggle (Grid / Table) */}
                                        <div className="flex items-center rounded-md border border-gray-200 overflow-hidden">
                                            {/* Grid button */}
                                            <button
                                                type="button"
                                                onClick={() => setAssetViewMode("grid")}
                                                title="Grid view"
                                                className={`flex items-center gap-1 px-3.5 py-1.5 text-xs md:text-sm transition-colors duration-150
                                            ${assetViewMode === "grid"
                                                        ? "bg-black text-white font-medium"
                                                        : "bg-white text-gray-500 hover:bg-black/80 hover:text-white"
                                                    }
                                        `}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                    strokeWidth={2}
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M4 4h7v7H4V4zm9 0h7v7h-7V4zM4 13h7v7H4v-7zm9 0h7v7h-7v-7z"
                                                    />
                                                </svg>
                                                <span className="hidden sm:inline">{t("assetManagement.view.grid")}</span>
                                            </button>

                                            {/* Table button */}
                                            <button
                                                type="button"
                                                onClick={() => setAssetViewMode("table")}
                                                title="Table view"
                                                className={`flex items-center gap-1 px-3.5 py-1.5 text-xs md:text-sm border-l border-gray-200 transition-colors duration-150
                                            ${assetViewMode === "table"
                                                        ? "bg-black text-white font-medium"
                                                        : "bg-white text-gray-500 hover:bg-black/80 hover:text-white"
                                                    }
                                        `}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                    strokeWidth={2}
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M3 6h18M3 12h18M3 18h18"
                                                    />
                                                </svg>
                                                <span className="hidden sm:inline">{t("assetManagement.view.table")}</span>
                                            </button>
                                        </div>


                                        {/* Add button */}
                                        <button
                                            onClick={handleAddNew}
                                            className="
                                        flex items-center justify-center gap-1
                                        h-8 px-3
                                        rounded-md
                                        bg-[#0F0F19]
                                        text-white
                                        text-sm
                                        border border-[#0F0F19]
                                        hover:opacity-90
                                        transition
                                    "
                                        >
                                            <Plus className="w-4 h-4" />
                                            <span className="hidden sm:inline">{t("assetManagement.add")}</span>
                                        </button>
                                    </div>
                                </div>


                                {assetViewMode === "table" ? (
                                    <AssetTable
                                        data={filteredAssets}
                                        totalPagesServer={1}
                                        currentPage2={1}
                                        pageSize2={10}
                                        onEdit={handleEdit}
                                        onDelete={handleDelete}
                                        onDuplicate={handleDuplicate}
                                    />
                                ) : (
                                    <AssetGrid
                                        data={filteredAssets}
                                        onEdit={handleEdit}
                                        onDelete={handleDelete}
                                    />
                                )}
                                <div className="h-16" />
                            

                            {/* Overlay */}
                            <Popup
                                open={isOverlayOpen}
                                onClose={handleClose}
                                modal
                                nested
                                contentStyle={{
                                    width: "80%",
                                    maxWidth: "800px",
                                    padding: "0",
                                    borderRadius: "8px",
                                }}
                            >
                                {(close) => (
                                    <AssetOverlay
                                        close={handleClose}
                                        asset={selectedAsset}
                                    />
                                )}
                            </Popup>
                        </>
                    )}

                    {/* STATS VIEW */}
                    {viewOption === "Stats" && (
                        <>



                            {/* Header row */}
                            <div className="flex items-center justify-between">
                                <div>
                                    <h1 className="text-2xl text-[#0E0E0E] font-medium">
                                        {t("assetManagement.statsPage.title")}
                                    </h1>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {t("assetManagement.statsPage.subtitle")}
                                    </p>
                                </div>

                                <div className="flex items-center gap-2">
                                    {/* Assets tab */}
                                    <button
                                        onClick={handleViewAssets}
                                        className={`
      flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition
      ${viewOption === "Assets"
                                                ? "bg-[#0F0F19] text-white"
                                                : "bg-white text-gray-700 hover:bg-gray-100"
                                            }
    `}
                                    >
                                        {/* Icon */}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                                        </svg>
                                        <span>{t("assetManagement.tabs.assets")}</span>
                                    </button>


                                    <button
                                        onClick={handleViewMap}
                                        className={`
      flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition
      ${viewOption === "Assets"
                                                ? "bg-[#0F0F19] text-white"
                                                : "bg-white text-gray-700 hover:bg-gray-100"
                                            }
    `}
                                    >
                                        {/* Icon */}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                                        </svg>
                                        <span>{t("assetManagement.tabs.dependencyMap")}</span>
                                    </button>

                                    {/* Stats tab */}
                                    <button
                                        onClick={handleViewStats}
                                        className={`
      flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition
      ${viewOption === "Stats"
                                                ? "bg-[#0F0F19] text-white"
                                                : "bg-white text-gray-700 hover:bg-gray-100"
                                            }
    `}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                        <span>{t("assetManagement.tabs.stats")}</span>
                                    </button>
                                </div>

                            </div>

                            {/* Top metrics row (4 cards) */}
                            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                                {/* Donut / category */}
                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                                    <AssetMetric
                                        height={(window.innerHeight - 580) / 2}
                                        data={assets_data.data}
                                    />
                                </div>

                                {/* Status bar chart */}
                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                                    <AssetStatusMetric
                                        height={(window.innerHeight - 580) / 2}
                                        data={assets_data.status}
                                    />
                                </div>

                                {/* Quarter trend */}
                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                                    <AssetQuarterMetric
                                        height={(window.innerHeight - 580) / 2}
                                        data={assets_data.quarters}
                                    />
                                </div>

                                {/* Top assets list */}
                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                                    <TopAssets
                                        height={(window.innerHeight - 580) / 2}
                                        assets={assets_data.topAssets}
                                    />
                                </div>
                            </div>

                            {/* Map in full-width white card */}
                            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex-1 mt-4">
                                <AssetMap rawData={assets_data.location} />
                            </div>
                        </>
                    )}

                    

                    {/* Map View */}
                    {viewOption === "Map" && (
                        <div className="flex flex-col gap-6">


                            {/* Header row */}
                            <div className="flex items-center justify-between">
                                <div>
                                    <h1 className="text-2xl text-[#0E0E0E] font-medium">
                                        {t("assetManagement.dependencyMapPage.title")}
                                    </h1>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {t("assetManagement.dependencyMapPage.subtitle")}
                                    </p>
                                </div>

                                <div className="flex items-center gap-2">
                                    {/* Assets tab */}
                                    <button
                                        onClick={handleViewAssets}
                                        className={`
      flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition
      ${viewOption === "Assets"
                                                ? "bg-[#0F0F19] text-white"
                                                : "bg-white text-gray-700 hover:bg-gray-100"
                                            }
    `}
                                    >
                                        {/* Icon */}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                                        </svg>
                                        <span>{t("assetManagement.tabs.assets")}</span>
                                    </button>


                                    <button
                                        onClick={handleViewMap}
                                        className={`
      flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition
      ${viewOption === "Map"
                                                ? "bg-[#0F0F19] text-white"
                                                : "bg-white text-gray-700 hover:bg-gray-100"
                                            }
    `}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>

                                        <span>{t("assetManagement.tabs.dependencyMap")}</span>
                                    </button>

                                    {/* Stats tab */}
                                    <button
                                        onClick={handleViewStats}
                                        className={`
      flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition
      ${viewOption === "Stats"
                                                ? "bg-[#0F0F19] text-white"
                                                : "bg-white text-gray-700 hover:bg-gray-100"
                                            }
    `}
                                    >
                                        {/* Icon */}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                                        </svg>
                                        <span>{t("assetManagement.tabs.stats")}</span>
                                    </button>
                                </div>

                            </div>

                            {/* ===== STAT CARDS (ABOVE THE MAP WRAPPER) ===== */}
                            <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-4 flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                                        <Box className="text-blue-600" size={20} />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">{t("assetManagement.summary.totalAssets")}</p>
                                        <p className="text-2xl font-semibold text-gray-900">
                                            {assets?.length ?? 0}
                                        </p>
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-4 flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                                        <Link2 className="text-purple-600" size={20} />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">{t("assetManagement.dependencyMapPage.mapStats.totalConnections")}</p>
                                        <p className="text-2xl font-semibold text-gray-900">
                                            {assets_data?.totalConnections ?? 0}
                                        </p>
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-4 flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center">
                                        <AlertCircle className="text-orange-600" size={20} />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">{t("assetManagement.dependencyMapPage.mapStats.criticalAssets")}</p>
                                        <p className="text-2xl font-semibold text-gray-900">
                                            {assets_data?.criticalAssetsCount ?? 0}
                                        </p>
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-4 flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                                        <TrendingUp className="text-emerald-600" size={20} />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">{t("assetManagement.dependencyMapPage.mapStats.avgDependencies")}</p>
                                        <p className="text-2xl font-semibold text-gray-900">
                                            {assets_data?.avgDependencies
                                                ? Number(assets_data.avgDependencies).toFixed(2)
                                                : "0"}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* ===== MAP WRAPPER ===== */}
                            <div className="relative z-0 bg-white rounded-2xl border border-gray-200 shadow-sm">
                                <DependencyMap heightClass="h-[650px]" />
                            </div>

                            {/* TABLE BELOW MAP */}
                            <div className="relative z-0">
                                <SpofPanel
                                    assets={assets}
                                    //onViewMap={(assetId) => console.log("focus", assetId)}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );


};




const mapStateToProps = (state) => {
    console.log("Assets", state.asset.assets)
    return {
        assets: state.asset.assets,
        //loading: state.asset.loading,
        error: state.asset.error,
        sidebar_collapsed: state.auth.sidebar_collapsed,
        assets_data: state.asset.assets_stat_data,
        department_id: state.department.activeDepartmentId
    }
}

const mapDispatchToProps = (dispatch) => ({
    getInventoryFN: (dep) => dispatch(getInventory(dep)),
    deleteAssetFN: (id) => dispatch(deleteAsset(id)),
    getAssetStatsFN: (dep) => dispatch(getAssetStats(dep)),
    duplicateAssetFN:(id) => dispatch(duplicateAsset(id))
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(ITInventory);